/**
 * IFD Popup Modal
 * Extends the Enterprise theme's global `Modal` class (defined in main.js) so it
 * reuses the theme's focus trap, ESC handling, overlay-click close and ARIA
 * behaviour. Only the delay trigger and the "show once per period" cookie logic
 * (configurable in hours) are added here.
 *
 * Wrapped in an IIFE so the cookie helpers don't leak into the global scope and
 * clash with the theme's own `setCookie`/`getCookie` (pop-up.js).
 */
(() => {
  /**
   * Sets a cookie that expires after a number of hours.
   * @param {string} name - Cookie name.
   * @param {string} value - Cookie value.
   * @param {number} hours - Hours until the cookie expires.
   */
  const setCookieHours = (name, value, hours) => {
    let expires = '';

    if (hours) {
      const date = new Date();
      date.setTime(date.getTime() + hours * 60 * 60 * 1000);
      expires = `; expires=${date.toUTCString()}`;
    }

    document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}${expires}; path=/; SameSite=Lax`;
  };

  /**
   * Gets the value of a cookie (if it exists).
   * @param {string} name - Cookie name.
   * @returns {?string}
   */
  const getCookieValue = (name) => {
    const cookieString = `; ${document.cookie}`;
    const cookies = cookieString.split(`; ${encodeURIComponent(name)}=`);

    if (cookies.length === 2) {
      return cookies.pop().split(';').shift();
    }

    return null;
  };

  /* global Modal, Shopify, theme */

  if (customElements.get('ifd-popup-modal')) return;

  // `Modal` is defined alongside the `modal-dialog` element in the theme's main.js.
  customElements.whenDefined('modal-dialog').then(() => {
    class IfdPopupModal extends Modal {
      constructor() {
        super();
        this.cookieName = `${this.id}-dismissed`;
      }

      connectedCallback() {
        // In the theme editor, open/close with the section so it can be styled.
        if (Shopify.designMode) {
          this.designSelectHandler = (evt) => {
            if (evt.target === this.closest('.shopify-section')) this.open();
          };
          this.designDeselectHandler = this.close.bind(this);

          document.addEventListener('shopify:section:select', this.designSelectHandler);
          document.addEventListener('shopify:section:deselect', this.designDeselectHandler);
          return;
        }

        // Disabled, already dismissed within the cookie window, or hidden on mobile.
        const showOnMobile = this.dataset.showOnMobile === 'true';
        const allowedOnViewport = showOnMobile || (window.theme && theme.mediaMatches.md);

        if (this.dataset.enabled !== 'true' || getCookieValue(this.cookieName) || !allowedOnViewport) {
          return;
        }

        const delay = Number(this.dataset.delay) || 0;
        this.openTimeout = setTimeout(() => this.open(), delay * 1000);
      }

      disconnectedCallback() {
        if (this.openTimeout) clearTimeout(this.openTimeout);

        if (this.designSelectHandler) {
          document.removeEventListener('shopify:section:select', this.designSelectHandler);
          document.removeEventListener('shopify:section:deselect', this.designDeselectHandler);
        }
      }

      /**
       * Opens the modal. Records the dismissal cookie as soon as the popup is
       * shown so it only appears once per configured period (default 24h),
       * regardless of how the visitor leaves the page. Closing via the X /
       * overlay / ESC therefore keeps it hidden for the same window.
       * @param {Element} [opener] - Element that opened the modal.
       */
      open(opener) {
        super.open(opener);

        if (!Shopify.designMode) {
          const hours = Number(this.dataset.cookieHours) || 24;
          setCookieHours(this.cookieName, 'true', hours);
        }
      }
    }

    customElements.define('ifd-popup-modal', IfdPopupModal);
  });
})();
